import { buildInvoiceFileName } from "@msys/common";
import { MenuButton } from "@msys/ui";
import { GetApp as DownloadIcon } from "@mui/icons-material";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { EInvoiceFormat } from "../../../clients/graphqlTypes.js";
import { useUserData } from "../../auth/useUserData.js";
import type { OpenFileOptions } from "../../commons/hooks/useOpenFile.js";

const FORMAT_LABELS: Record<EInvoiceFormat, string> = {
  XRECHNUNG_CII: "XRechnung (CII)",
  XRECHNUNG_UBL: "XRechnung (UBL)",
  ZUGFERD_EN16931: "ZUGFeRD EN 16931",
  ZUGFERD_EXTENDED: "ZUGFeRD Extended",
  ZUGFERD_XRECHNUNG: "ZUGFeRD XRechnung",
};

export function InvoiceDownloadMenu({
  pdfUrl,
  eInvoiceFileUrl,
  eInvoiceFormat,
  invoiceId,
  invoiceNumber,
  openPdf,
  openAnyFile,
}: {
  pdfUrl: string;
  eInvoiceFileUrl: string | null;
  eInvoiceFormat: EInvoiceFormat | null;
  invoiceId: string;
  invoiceNumber: string | null;
  openPdf: (
    url: string,
    filename: string,
    opts?: OpenFileOptions
  ) => Promise<void>;
  openAnyFile: (
    url: string,
    filename: string,
    opts?: OpenFileOptions
  ) => Promise<void>;
}) {
  const { t } = useTranslate(["Invoices", "Global"]);
  const { currentUser } = useUserData();
  const formatLabel = eInvoiceFormat ? FORMAT_LABELS[eInvoiceFormat] : null;
  const isXmlFormat =
    eInvoiceFormat === "XRECHNUNG_CII" || eInvoiceFormat === "XRECHNUNG_UBL";
  // The stored e-invoice file URL already carries its real extension
  // (".xml" for XRechnung, ".pdf" for ZUGFeRD), so trust that first. Only fall
  // back to the per-invoice format mapping when the URL has none — and never
  // force a ".pdf" extension for an invoice whose format was not stamped
  // (legacy rows predating per-invoice format stamping), which would mislabel
  // an XML file as PDF.
  const extFromUrl = eInvoiceFileUrl
    ?.split("?")[0]
    .match(/\.([a-z0-9]+)$/i)?.[1]
    ?.toLowerCase();
  const eInvoiceExt =
    extFromUrl ?? (eInvoiceFormat ? (isXmlFormat ? "xml" : "pdf") : "xml");

  const eInvoiceEnabled =
    currentUser?.organisation.eInvoiceSettings.enabled ?? false;

  return (
    <MenuButton
      Icon={<DownloadIcon />}
      tooltip={t("Download", { ns: "Global" })}
    >
      {(!!eInvoiceFileUrl || eInvoiceEnabled) && (
        <MenuItem
          disabled={!eInvoiceFileUrl}
          onClick={async e => {
            e.stopPropagation();
            if (eInvoiceFileUrl) {
              await openAnyFile(
                eInvoiceFileUrl,
                buildInvoiceFileName({
                  number: invoiceNumber,
                  id: invoiceId,
                  kind: "e-invoice",
                  ext: eInvoiceExt === "pdf" ? "pdf" : "xml",
                }),
                { filenameFromResponse: true }
              );
            }
          }}
        >
          <ListItemIcon style={{ minWidth: 36 }}>
            <DownloadIcon />
          </ListItemIcon>
          <ListItemText
            primary={t("Download e-invoice", { ns: "Invoices" })}
            // Only surface the format subtitle when a file actually exists; on
            // the disabled (no-file) state it would imply a downloadable format
            // is available and could contradict an inconsistent stamped format.
            secondary={eInvoiceFileUrl ? formatLabel : undefined}
          />
        </MenuItem>
      )}
      <MenuItem
        onClick={async e => {
          e.stopPropagation();
          await openPdf(
            pdfUrl,
            buildInvoiceFileName({
              number: invoiceNumber,
              id: invoiceId,
              kind: "pdf",
            }),
            { filenameFromResponse: true }
          );
        }}
      >
        <ListItemIcon style={{ minWidth: 36 }}>
          <DownloadIcon />
        </ListItemIcon>
        <ListItemText primary={t("Download plain PDF", { ns: "Invoices" })} />
      </MenuItem>
    </MenuButton>
  );
}
